import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, StatusBadge } from "@/components/common/Primitives";
import { DataTable } from "@/components/common/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtEUR, fmtDate } from "@/lib/format";
import { MF_VERSION_STATUS_MAP, MF_COMMISSION_TYPES } from "@/lib/constants";
import { Plus, Calculator, Send, Check, X, GitBranch } from "lucide-react";

const CT_LABEL = Object.fromEntries(MF_COMMISSION_TYPES.map((c) => [c.value, c.label]));

export default function MfCommissionRules() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const models = can(user?.role, "mobilfunk:models");
  const [q, setQ] = useState("");
  const [create, setCreate] = useState(false);
  const [detail, setDetail] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["mf-rules", q], queryFn: async () => (await api.get(`/mobilfunk/commission-rules?q=${encodeURIComponent(q)}`)).data });

  const columns = [
    { key: "name", header: "Provisionsmodell", render: (r) => (
      <div className="min-w-0"><div className="flex items-center gap-2 font-medium">{r.name}{r.is_demo && <Badge variant="outline" className="text-[10px]">DEMO</Badge>}</div>
        <div className="text-xs text-muted-foreground">{CT_LABEL[r.commission_type] || r.commission_type} · Prio {r.priority} · {r.provider_name || "alle Anbieter"}</div></div>
    ) },
    { key: "value", header: "Wert", render: (r) => r.percent ? `${r.percent} %` : fmtEUR(r.amount) },
    { key: "versions", header: "Versionen", mobileHidden: true, render: (r) => <Badge variant="secondary">{r.version_count}</Badge> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.current_status} map={MF_VERSION_STATUS_MAP} /> },
  ];

  return (
    <div data-testid="mf-rules">
      <PageHeader title="Provisionsmodelle" description={`${data?.total ?? 0} Modelle`}
        actions={models && <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setCreate(true)} data-testid="new-rule-button"><Plus className="h-4 w-4" />Neues Modell</Button>} />
      <div className="mb-4"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" className="sm:max-w-sm" data-testid="rule-search" /></div>
      <DataTable columns={columns} rows={data?.items} loading={isLoading} testIdPrefix="rule-row" onRowClick={(r) => setDetail(r.id)} />
      <CreateRuleDialog open={create} onClose={() => setCreate(false)} onDone={(id) => { setCreate(false); qc.invalidateQueries({ queryKey: ["mf-rules"] }); setDetail(id); }} />
      <RuleDrawer rid={detail} onClose={() => setDetail(null)} models={models} canApprove={can(user?.role, "mobilfunk:approve")} onChanged={() => qc.invalidateQueries({ queryKey: ["mf-rules"] })} />
    </div>
  );
}

const RVEMPTY = { valid_from: "", valid_to: "", commission_type: "basis", amount: 0, percent: 0, base: "grundgebuehr", recurring_months: 0, change_summary: "", conditions: { customer_type: "", channel: "" }, combinable: true };

function CommissionFields({ v, setV, idPrefix }) {
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const isPct = ["prozent"].includes(v.commission_type);
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Provisionsart</Label>
          <Select value={v.commission_type} onValueChange={(val) => set("commission_type", val)}>
            <SelectTrigger data-testid={`${idPrefix}-type`}><SelectValue /></SelectTrigger>
            <SelectContent>{MF_COMMISSION_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select></div>
        {isPct
          ? <div className="space-y-1.5"><Label>Prozent %</Label><Input type="number" step="0.01" value={v.percent} onChange={(e) => set("percent", e.target.value)} data-testid={`${idPrefix}-percent`} /></div>
          : <div className="space-y-1.5"><Label>Betrag €</Label><Input type="number" step="0.01" value={v.amount} onChange={(e) => set("amount", e.target.value)} data-testid={`${idPrefix}-amount`} /></div>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Gültig ab</Label><Input type="date" value={v.valid_from} onChange={(e) => set("valid_from", e.target.value)} data-testid={`${idPrefix}-validfrom`} /></div>
        <div className="space-y-1.5"><Label>Kundentyp</Label>
          <Select value={v.conditions?.customer_type || "all"} onValueChange={(val) => set("conditions", { ...(v.conditions || {}), customer_type: val === "all" ? "" : val })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Alle</SelectItem><SelectItem value="privat">Privat</SelectItem><SelectItem value="geschaeft">Geschäft</SelectItem></SelectContent>
          </Select></div>
      </div>
    </>
  );
}

function CreateRuleDialog({ open, onClose, onDone }) {
  const [h, setH] = useState({ name: "", rule_type: "basis", priority: 100, provider_id: "", distributor_id: "" });
  const [v, setV] = useState(RVEMPTY);
  const submit = async (e) => {
    e.preventDefault();
    if (!h.name.trim()) return toast.error("Name erforderlich");
    if (!v.valid_from) return toast.error("Gültig-ab-Datum erforderlich");
    try {
      const { data } = await api.post("/mobilfunk/commission-rules", { ...h, priority: Number(h.priority) || 100, rule_type: v.commission_type });
      await api.post(`/mobilfunk/commission-rules/${data.id}/versions`, { ...v, amount: Number(v.amount) || 0, percent: Number(v.percent) || 0 });
      toast.success("Provisionsmodell angelegt (Entwurf v1)"); setH({ name: "", rule_type: "basis", priority: 100, provider_id: "", distributor_id: "" }); setV(RVEMPTY);
      onDone(data.id);
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="create-rule-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Calculator className="h-4 w-4" />Neues Provisionsmodell</DialogTitle>
          <DialogDescription>Kopfdaten + erste Regelversion (Entwurf). Freigabe erfolgt anschließend.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={h.name} onChange={(e) => setH((s) => ({ ...s, name: e.target.value }))} data-testid="rule-name-input" /></div>
          <div className="space-y-1.5"><Label>Priorität</Label><Input type="number" value={h.priority} onChange={(e) => setH((s) => ({ ...s, priority: e.target.value }))} /></div>
          <div className="rounded-md border border-border p-3 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Erste Regelversion</div>
            <CommissionFields v={v} setV={setV} idPrefix="rule" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="rule-create-save">Anlegen</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RuleDrawer({ rid, onClose, models, canApprove, onChanged }) {
  const qc = useQueryClient();
  const [addVer, setAddVer] = useState(false);
  const { data } = useQuery({ queryKey: ["mf-rule", rid], queryFn: async () => (await api.get(`/mobilfunk/commission-rules/${rid}`)).data, enabled: !!rid });
  const r = data?.rule;
  const versions = data?.versions || [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ["mf-rule", rid] }); onChanged(); };
  const action = async (vid, act) => {
    try { await api.post(`/mobilfunk/commission-rule-versions/${vid}/${act}`); toast.success("Aktualisiert"); refresh(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Sheet open={!!rid} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="rule-drawer">
        <SheetHeader><SheetTitle className="flex items-center gap-2"><Calculator className="h-4 w-4" />{r?.name}</SheetTitle></SheetHeader>
        {r && (
          <div className="mt-4 space-y-4">
            <div className="text-sm text-muted-foreground">Priorität {r.priority}</div>
            {models && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setAddVer(true)} data-testid="add-rule-version"><GitBranch className="h-4 w-4" />Neue Version</Button>}
            <div className="space-y-2">
              {versions.map((v) => (
                <div key={v.id} className="rounded-lg border border-border p-3" data-testid={`rule-version-${v.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium">v{v.version_no} · {CT_LABEL[v.commission_type] || v.commission_type} · {v.percent ? `${v.percent} %` : fmtEUR(v.amount)}</div>
                    <StatusBadge status={v.status} map={MF_VERSION_STATUS_MAP} />
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    gültig ab {fmtDate(v.valid_from)}{v.valid_to ? `–${fmtDate(v.valid_to)}` : ""}
                    {v.conditions?.customer_type ? ` · ${v.conditions.customer_type}` : ""}
                    {v.approved_by && <> · freigegeben von {v.approved_by}</>}
                  </div>
                  <div className="mt-2 flex gap-1.5">
                    {models && v.status === "entwurf" && <Button size="sm" variant="outline" className="gap-1" onClick={() => action(v.id, "submit")} data-testid={`rv-submit-${v.id}`}><Send className="h-3.5 w-3.5" />Einreichen</Button>}
                    {canApprove && v.status === "zur_freigabe" && <>
                      <Button size="sm" variant="outline" className="gap-1 text-emerald-600" onClick={() => action(v.id, "approve")} data-testid={`rv-approve-${v.id}`}><Check className="h-3.5 w-3.5" />Freigeben</Button>
                      <Button size="sm" variant="outline" className="gap-1 text-red-600" onClick={() => action(v.id, "reject")} data-testid={`rv-reject-${v.id}`}><X className="h-3.5 w-3.5" />Ablehnen</Button>
                    </>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <AddRuleVersionDialog open={addVer} rid={rid} onClose={() => setAddVer(false)} onDone={() => { setAddVer(false); refresh(); }} />
      </SheetContent>
    </Sheet>
  );
}

function AddRuleVersionDialog({ open, rid, onClose, onDone }) {
  const [v, setV] = useState(RVEMPTY);
  const submit = async (e) => {
    e.preventDefault();
    if (!v.valid_from) return toast.error("Gültig-ab-Datum erforderlich");
    try {
      await api.post(`/mobilfunk/commission-rules/${rid}/versions`, { ...v, amount: Number(v.amount) || 0, percent: Number(v.percent) || 0 });
      toast.success("Version angelegt (Entwurf)"); setV(RVEMPTY); onDone();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="add-rule-version-dialog">
        <DialogHeader><DialogTitle>Neue Regelversion</DialogTitle>
          <DialogDescription>Neue Regeln verändern keine historischen Berechnungen.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <CommissionFields v={v} setV={setV} idPrefix="arv" />
          <div className="space-y-1.5"><Label>Änderungszusammenfassung</Label><Input value={v.change_summary} onChange={(e) => setV((s) => ({ ...s, change_summary: e.target.value }))} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="arv-save">Anlegen</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
